import { useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  LogOut,
  LayoutDashboard,
  FilePlus2,
  ListChecks,
  Users,
  Building2,
  Layers,
  FileBarChart2,
  ScrollText,
  ShieldCheck,
  Wallet,
  HandCoins,
  ReceiptText,
  Settings,
  Gift,
  ChevronDown,
  Trophy,
  UsersRound,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { NavLink } from "@/components/NavLink";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useNomeBonificacao } from "@/hooks/useNomeBonificacao";
import { useContadoresSidebar, type ContadoresSidebar } from "@/hooks/useContadoresSidebar";

interface Item {
  title: string;
  url: string;
  icon: LucideIcon;
  end: boolean;
  /** Qual contador da sidebar aparece em pílula neste item. */
  contador?: keyof ContadoresSidebar;
}

interface Secao {
  /** Sem rótulo: os itens aparecem direto sob a categoria. */
  label?: string;
  items: Item[];
}

interface Categoria {
  id: string;
  label: string;
  abertaPorPadrao: boolean;
  secoes: Secao[];
}

/** Área pessoal do PJ: emite NF todo mês e acompanha o demonstrativo. */
const pessoalPJ: Item[] = [
  { title: "Dashboard", url: "/colaborador", icon: LayoutDashboard, end: true },
  { title: "Enviar NF", url: "/colaborador/nova", icon: FilePlus2, end: false },
  { title: "Minhas NFs", url: "/colaborador/notas", icon: ListChecks, end: false },
  { title: "Meu Demonstrativo", url: "/colaborador/demonstrativo", icon: ReceiptText, end: false },
];

/** CLT não emite NF: vê o que recebe e o holerite. */
const pessoalCLT: Item[] = [
  { title: "Dashboard", url: "/colaborador", icon: LayoutDashboard, end: true },
  { title: "Meu Holerite", url: "/colaborador/demonstrativo", icon: ReceiptText, end: false },
];

/** O nome da bonificação é configurável (ex.: "Múltiplos de Salário"). */
const itemBonificacao = (nome: string): Item => ({ title: nome, url: "/rh/bonificacao", icon: Trophy, end: false });

const secoesRH = (nomeBonificacao: string): Secao[] => [
  {
    label: "Visão Geral",
    items: [
      { title: "Dashboard", url: "/rh", icon: LayoutDashboard, end: true },
      { title: "Headcount", url: "/rh/headcount", icon: UsersRound, end: false },
      { title: "Relatórios", url: "/rh/relatorio", icon: FileBarChart2, end: false },
      { title: "Notas Fiscais", url: "/rh/notas", icon: ScrollText, end: false, contador: "notas" },
    ],
  },
  {
    label: "Folha e Pagamentos",
    items: [
      { title: "Folha de Pagamento", url: "/rh/folha", icon: Wallet, end: false, contador: "folha" },
      itemBonificacao(nomeBonificacao),
      { title: "Benefícios", url: "/rh/beneficios", icon: Gift, end: false },
      { title: "Empréstimos & Descontos", url: "/rh/emprestimos", icon: HandCoins, end: false },
    ],
  },
  {
    label: "Cadastros",
    items: [
      { title: "Usuários", url: "/rh/colaboradores", icon: Users, end: false, contador: "usuarios" },
      { title: "Tomadores", url: "/rh/tomadores", icon: Building2, end: false },
      { title: "Departamentos", url: "/rh/departamentos", icon: Layers, end: false },
    ],
  },
  {
    label: "Administração",
    items: [
      { title: "Auditoria", url: "/rh/auditoria", icon: ShieldCheck, end: false },
      { title: "Configurações", url: "/rh/configuracoes", icon: Settings, end: false },
    ],
  },
];

/** Financeiro só visualiza: mesmas telas de hoje, sem cadastros nem administração. */
const secoesFinanceiro = (nomeBonificacao: string): Secao[] => [
  {
    label: "Visão Geral",
    items: [
      { title: "Relatórios", url: "/rh/relatorio", icon: FileBarChart2, end: false },
      { title: "Notas Fiscais", url: "/rh/notas", icon: ScrollText, end: false },
    ],
  },
  {
    label: "Folha e Pagamentos",
    items: [
      { title: "Folha de Pagamento", url: "/rh/folha", icon: Wallet, end: false },
      itemBonificacao(nomeBonificacao),
      { title: "Empréstimos & Descontos", url: "/rh/emprestimos", icon: HandCoins, end: false },
    ],
  },
];

const CHAVE_CATEGORIAS = "sidebar:categorias";

/** O AppLayout é montado por página, então o aberto/fechado precisa sobreviver à navegação. */
function lerCategoriasSalvas(): Record<string, boolean> {
  try {
    return JSON.parse(localStorage.getItem(CHAVE_CATEGORIAS) ?? "{}") ?? {};
  } catch {
    return {};
  }
}

function salvarCategorias(estado: Record<string, boolean>) {
  try {
    localStorage.setItem(CHAVE_CATEGORIAS, JSON.stringify(estado));
  } catch {
    // Sem storage: só não lembra entre páginas.
  }
}

function contemRota(categoria: Categoria, pathname: string) {
  return categoria.secoes.some((s) =>
    s.items.some((i) => (i.end ? pathname === i.url : pathname === i.url || pathname.startsWith(`${i.url}/`))),
  );
}

const ListaItens = ({
  items,
  collapsed,
  contadores,
}: {
  items: Item[];
  collapsed: boolean;
  contadores: ContadoresSidebar | null;
}) => (
  <SidebarMenu className="gap-0.5">
    {items.map((item) => {
      const n = item.contador && contadores ? contadores[item.contador] : 0;
      return (
        <SidebarMenuItem key={item.url + item.title}>
          <SidebarMenuButton asChild tooltip={n ? `${item.title} (${n})` : item.title} className="h-[34px] rounded-[7px] px-2.5 text-sm">
            {/* end=false nas listas: o detalhe (/rh/folha/:id) deixa o item pai ativo. */}
            <NavLink
              to={item.url}
              end={item.end}
              className="group/item relative flex items-center gap-2.5 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground"
              activeClassName="active bg-sidebar-accent font-semibold !text-sidebar-primary-glow [&>.barra]:block"
            >
              <span className="barra absolute -left-2 bottom-2 top-2 hidden w-[3px] rounded-r-[3px] bg-sidebar-primary group-data-[collapsible=icon]:hidden" />
              <item.icon className="h-4 w-4 opacity-80 group-[.active]/item:opacity-100" />
              {!collapsed && <span className="flex-1 truncate">{item.title}</span>}
              {!collapsed && n > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-sidebar-primary/[.18] px-1.5 text-[11px] font-semibold tabular-nums text-sidebar-primary-glow group-[.active]/item:bg-sidebar-primary group-[.active]/item:text-white">
                  {n}
                </span>
              )}
            </NavLink>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    })}
  </SidebarMenu>
);

export const AppSidebar = () => {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const ehClt = user?.vinculo === "clt";
  const pessoal = ehClt ? pessoalCLT : pessoalPJ;
  const nomeBonificacao = useNomeBonificacao();
  const contadores = useContadoresSidebar(user?.role === "rh");

  // Categorias macro só existem para RH e Financeiro; o colaborador vê a lista direta.
  const categorias: Categoria[] =
    user?.role === "rh" || user?.role === "financeiro"
      ? [
          { id: "colaborador", label: "Colaborador", abertaPorPadrao: true, secoes: [{ items: pessoal }] },
          user.role === "rh"
            ? { id: "gestao", label: "Gente e Gestão", abertaPorPadrao: false, secoes: secoesRH(nomeBonificacao) }
            : { id: "financeiro", label: "Financeiro", abertaPorPadrao: false, secoes: secoesFinanceiro(nomeBonificacao) },
        ]
      : [];

  const [abertas, setAbertas] = useState<Record<string, boolean>>(() => {
    const salvas = lerCategoriasSalvas();
    return Object.fromEntries(
      categorias.map((c) => [
        c.id,
        // A categoria da página atual sempre abre, senão o item ativo fica escondido.
        contemRota(c, pathname) || (salvas[c.id] ?? c.abertaPorPadrao),
      ]),
    );
  });

  const alternar = (id: string, aberta: boolean) => {
    setAbertas((atual) => {
      const novo = { ...atual, [id]: aberta };
      salvarCategorias({ ...lerCategoriasSalvas(), ...novo });
      return novo;
    });
  };

  const handleLogout = () => {
    logout();
    nav("/login", { replace: true });
  };

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-16 justify-center border-b border-sidebar-border px-4 py-0">
        <Logo compact={collapsed} />
      </SidebarHeader>
      <SidebarContent>
        {categorias.length === 0 ? (
          <SidebarGroup>
            <SidebarGroupLabel>{ehClt ? "Colaborador CLT" : "Colaborador PJ"}</SidebarGroupLabel>
            <SidebarGroupContent>
              <ListaItens items={pessoal} collapsed={collapsed} contadores={null} />
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          categorias.map((categoria) => (
            <Collapsible
              key={categoria.id}
              // Com a barra recolhida em ícones não há cabeçalho para clicar: mostra tudo.
              open={collapsed || !!abertas[categoria.id]}
              onOpenChange={(aberta) => alternar(categoria.id, aberta)}
              className="group/categoria"
            >
              <SidebarGroup className="py-1">
                <SidebarGroupLabel
                  asChild
                  className="h-8 text-[11px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/75 hover:bg-sidebar-accent"
                >
                  <CollapsibleTrigger className="flex w-full items-center">
                    {categoria.label}
                    <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/categoria:rotate-180" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  {categoria.secoes.map((secao, idx) => (
                    <SidebarGroupContent key={secao.label ?? idx} className={cn(secao.label && "mt-2")}>
                      {secao.label && (
                        <SidebarGroupLabel className="h-6 pl-2.5 text-[11px] font-medium text-sidebar-foreground/50">
                          {secao.label}
                        </SidebarGroupLabel>
                      )}
                      <ListaItens items={secao.items} collapsed={collapsed} contadores={contadores} />
                    </SidebarGroupContent>
                  ))}
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          ))
        )}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} className="h-[34px] rounded-[7px] px-2.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground">
              <LogOut className="h-4 w-4" />
              {!collapsed && <span>Sair</span>}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};
